import { HttpClient } from '@adapters/HttpClient';
import { AffiliatesCode, AffiliatesCommission } from '../models/Affiliates';

export interface AffiliatesFilters {
  searchTerm?: string;
  type_ids?: number[];
  product_ids?: number[];
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  event_ids?: number[];
}

export interface AffiliatesCommissionsPage {
  affiliates: AffiliatesCommission[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

export interface AffiliatesTotals {
  total_commissions: number;
  total_invoice_amount: number;
  average_invoice_amount: number;
  total_commission_amount: number;
}

export class AffiliatesApiAdapter {
  constructor(private httpClient: HttpClient) { }

  async getAffiliatesPaginated(
    filters: AffiliatesFilters = {}
  ): Promise<AffiliatesCommissionsPage> {
    const {
      searchTerm = '',
      product_ids,
      sortBy = 'id',
      sortDirection = 'desc',
      page = 1,
      limit = 15,
    } = filters;

    const params: any = {
      ...(searchTerm && { search: searchTerm }),
      ...(product_ids && { product_ids }),
      ...(sortBy && { order_by: sortBy }),
      ...(sortDirection && { order_direction: sortDirection }),
      page,
      per_page: limit,
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>(
      '/affiliates/commissions',
      params,
      {},
      showGlobalLoading
    );

    return {
      affiliates: response.data as AffiliatesCommission[],
      nextPage:
        response.meta.currentPage < response.meta.lastPage
          ? response.meta.currentPage + 1
          : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async getAll(): Promise<AffiliatesCommission[]> {
    const response = await this.getAffiliatesPaginated();
    return response.affiliates;
  }

  async getAffiliatesTotals(params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<AffiliatesTotals> {
    const response = await this.httpClient.get<any>(
      '/affiliates/totals',
      params
    );
    return response;
  }

  async getAffiliateCode(): Promise<AffiliatesCode | undefined> {
    // Descomenta la siguiente línea para simular que el usuario NO tiene código de afiliado:
    //return undefined;
    // Lógica real:
    const response = await this.httpClient.get<any>('/affiliates/codes');
    return response.data as AffiliatesCode;
  }

  async createAffiliateCode(name: string): Promise<{ id: number }> {
    const response = await this.httpClient.post<any>('/affiliates/codes', {
      name,
    });
    return response;
  }
}
